import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { colors, fonts, radius } from '../../theme';
import { supabase } from '../../supabase';
import { listScans, restoreScan } from '../../services/bodyScanService';
import { clientSharesHealth } from '../../services/healthSharingService';
import { weighIns as loadWeighIns } from '../../services/weightService';
import { pointsFor, latestWithChange, compareScans, formatChange } from '../../utils/bodyStats';
import { MetricTile, Button, Notice, Dialog, mono, fmtDay } from './parts';
import ScanChart from './ScanChart';
import ScanHistory from './ScanHistory';
import ScanDetail from './ScanDetail';
import ScanForm from './ScanForm';
import PhotoScan from './PhotoScan';

// Reach year 1 through the month-based loader so no account history is excluded.
const FULL_HISTORY_MONTHS = (new Date().getFullYear() - 1) * 12;

/**
 * Body stats (plan 008, design: docs/design/body-stats/v2/SPEC.md), inside Check-Ins for the client and
 * the client's Check-ins tab for their coach. Weight from every weigh-in (one weight record, migration
 * 080, a scan's weight among them); body fat and muscle from scans (migration 081), compared on the same
 * machine. Typed entry now; reading a photo of the sheet comes next (plan 008, step 5).
 *
 * props: clientId (auth id), viewer 'client' | 'coach', clientName (for the coach), wide (computer layout)
 */
export default function BodyStatsSection({ clientId, viewer = 'client', clientName = 'the client', wide = false }) {
  const [scans, setScans] = useState([]);
  const [weighIns, setWeighIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [metric, setMetric] = useState('weight');
  const [machineFilter, setMachineFilter] = useState('all');
  const [dialog, setDialog] = useState(null); // { kind: 'add' } | { kind: 'detail', scan } | { kind: 'edit', scan }
  const [undo, setUndo] = useState(null);     // a just-deleted scan
  const [me, setMe] = useState(viewer === 'client' ? clientId : null);
  // For the coach: has the client agreed to share body scans (migration 083)? Without it the database
  // shows staff none of their scans; the weight record stays visible as before.
  const [shared, setShared] = useState(viewer === 'client' ? true : null);

  // Only the latest load for the client on screen may fill it: another client's, or an older one, never
  // does (Codex's review of migration 083, S4)
  const current = useRef({ clientId, n: 0 });
  const load = useCallback(async () => {
    const mine = { clientId, n: current.current.n + 1 };
    current.current = mine;
    setFailed(false);
    try {
      // Read the full account history, rather than a rolling two-year chart window.
      const [s, w] = await Promise.all([listScans(clientId), loadWeighIns(clientId, FULL_HISTORY_MONTHS)]);
      if (current.current !== mine) return;
      setScans(s);
      setWeighIns(w);
    } catch (e) {
      if (current.current !== mine) return;
      console.error('[BodyStats] load failed:', e);
      setFailed(true);
    } finally {
      if (current.current === mine) setLoading(false);
    }
  }, [clientId]);

  // Another client: nothing of the last one stays on screen while theirs loads
  useEffect(() => {
    setScans([]);
    setWeighIns([]);
    setLoading(true);
    setShared(viewer === 'client' ? true : null);
    load();
  }, [load, viewer]);
  useEffect(() => {
    if (viewer !== 'coach') return;
    supabase.auth.getUser().then(({ data }) => setMe(data?.user?.id ?? null)).catch(() => {});
  }, [viewer]);
  // The coach's screen asks again whenever it comes back into view, so a client who withdrew is no longer
  // shown on a screen left open (what was already seen cannot be unseen)
  useEffect(() => {
    if (viewer !== 'coach') return undefined;
    let gone = false;
    const check = () => {
      clientSharesHealth(clientId).then((v) => { if (!gone) setShared(v === true); }).catch(() => { if (!gone) setShared(false); });
    };
    check();
    const onBack = () => { if (document.visibilityState !== 'hidden') { check(); load(); } };
    window.addEventListener('focus', onBack);
    document.addEventListener('visibilitychange', onBack);
    return () => {
      gone = true;
      window.removeEventListener('focus', onBack);
      document.removeEventListener('visibilitychange', onBack);
    };
  }, [viewer, clientId, load]);

  const oldestFirst = useMemo(() => [...scans].reverse(), [scans]);
  const machines = useMemo(() => [...new Set(oldestFirst.map((s) => s.machine))], [oldestFirst]);
  const filtered = machineFilter === 'all' ? oldestFirst : oldestFirst.filter((s) => s.machine === machineFilter);
  const data = { scans: filtered, weighIns };
  const tiles = {
    weight: latestWithChange(pointsFor('weight', { scans: oldestFirst, weighIns })),
    fat: latestWithChange(pointsFor('fat', { scans: oldestFirst })),
    muscle: latestWithChange(pointsFor('muscle', { scans: oldestFirst })),
  };
  const latestScan = scans[0] ?? null;

  const whoEntered = (s) => {
    if (s.created_by === me) return 'You';
    if (s.created_by === clientId) return viewer === 'coach' ? clientName : 'You';
    return viewer === 'coach' ? 'Another coach' : 'Your coach';
  };
  const mayCorrect = (s) => viewer === 'client' || s.created_by === me;

  const afterSave = () => { setDialog(null); load(); };
  const afterDelete = (deleted) => { setDialog(null); setUndo(deleted); load(); };
  const doUndo = async () => {
    const s = undo;
    setUndo(null);
    try { await restoreScan(s.id); } catch (e) { console.error('[BodyStats] restore failed:', e); }
    load();
  };

  if (loading) return <div style={{ color: colors.textMuted, padding: 16, fontSize: 15 }}>Loading body stats…</div>;
  if (failed) {
    return (
      <Notice>Could not load body stats. Check your connection and <button type="button" onClick={load} style={{ background: 'none', border: 'none', color: colors.text, textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>try again</button>.</Notice>
    );
  }

  const addLabel = viewer === 'coach' ? `Add scan for ${clientName.split(' ')[0]}` : 'Add scan';
  const nothingYet = scans.length === 0 && weighIns.length === 0;
  const notShared = viewer === 'coach' && shared === false;
  const notSharedNote = notShared && (
    <div style={{ marginBottom: 16 }}><Notice>
      {clientName.split(' ')[0]} hasn't agreed to share body scans and health answers with their coach, so they aren't shown here.
      They can agree in the app's Settings at any time. Their weight is shown as before.
    </Notice></div>
  );

  return (
    <section aria-label="Body stats" style={{ fontFamily: fonts.body, color: colors.text, marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Body stats</h2>
        {latestScan && <div style={{ ...mono, fontSize: 12, color: colors.textMuted }}>{fmtDay(latestScan.measured_on)} · {latestScan.machine}</div>}
      </div>

      {notSharedNote}
      {nothingYet && notShared ? null : nothingYet ? (
        <div style={{ background: colors.surface1, border: `1px solid ${colors.border}`, borderRadius: radius.card, padding: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>No scans yet</div>
          <div style={{ fontSize: 15, lineHeight: 1.6, color: colors.textMuted, marginBottom: 16 }}>
            {viewer === 'coach'
              ? `When ${clientName.split(' ')[0]} adds a body scan, or you add one for them, their weight, body fat and muscle appear here.`
              : 'Had a body composition scan at the gym? Add its weight, body fat and muscle from the printed sheet. Any machine is fine. Your coach can add it for you too.'}
          </div>
          <Button kind="primary" style={{ width: '100%' }} onClick={() => setDialog({ kind: 'add' })}>{addLabel}</Button>
        </div>
      ) : (
        // On a computer, numbers and chart on the left; comparison and history on the right (SPEC, coach)
        <div style={wide ? { display: 'grid', gridTemplateColumns: '1.25fr 1fr', gap: 24, alignItems: 'start' } : undefined}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
            {['weight', 'fat', 'muscle'].map((k) => (
              <MetricTile key={k} metric={k} size={wide ? 'desktop' : 'phone'} selected={metric === k} onSelect={() => setMetric(k)}
                value={tiles[k]?.latest.value ?? null} change={tiles[k]?.change ?? null} notRecorded={tiles[k]?.notRecorded} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: colors.textMuted, margin: '8px 0 16px', textAlign: 'right' }}>
            Changes are since the previous reading from the same machine or scale.
          </div>

          {viewer === 'coach' && machines.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <label htmlFor="bs-machine" style={{ fontSize: 13, fontWeight: 600 }}>Machine</label>
              <select id="bs-machine" value={machineFilter} onChange={(e) => setMachineFilter(e.target.value)}
                style={{ minHeight: 44, background: colors.bg, color: colors.text, border: `1px solid ${colors.borderStrong}`, borderRadius: radius.sm, padding: '0 12px', fontFamily: fonts.body }}>
                <option value="all">All</option>
                {machines.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}

          <ScanChart metric={metric} points={pointsFor(metric, data)} width={wide ? 680 : 343} />

          {!notShared && (
            <div style={{ display: 'grid', gap: 4, margin: '16px 0' }}>
              <Button kind="primary" onClick={() => setDialog({ kind: 'add' })}>{addLabel}</Button>
            </div>
          )}
        </div>
        <div style={{ minWidth: 0 }}>

          {viewer === 'coach' && scans.length > 1 && <Compare scans={scans} />}

          {scans.length > 0 && (
            <>
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: wide ? '0 0 4px' : '24px 0 4px' }}>Scan history</h3>
              <ScanHistory scans={machineFilter === 'all' ? scans : scans.filter((s) => s.machine === machineFilter)}
                whoEntered={whoEntered} onOpen={(s) => setDialog({ kind: 'detail', scan: s })} />
            </>
          )}
          {scans.length === 0 && (
            <div style={{ fontSize: 13, color: colors.textMuted, lineHeight: 1.5 }}>
              Body fat and muscle appear after the first body scan.
            </div>
          )}
        </div>
        </div>
      )}

      {undo && (
        <div role="status" style={{ position: 'fixed', left: '50%', bottom: 88, transform: 'translateX(-50%)', zIndex: 1001, background: colors.surface3, border: `1px solid ${colors.borderStrong}`, borderRadius: radius.card, boxShadow: '0 12px 32px rgba(0,0,0,0.5)', padding: '8px 8px 8px 16px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          Scan deleted.
          <Button kind="quiet" onClick={doUndo}>Undo</Button>
          <Button kind="quiet" aria-label="Dismiss" onClick={() => setUndo(null)}>×</Button>
        </div>
      )}

      {dialog?.kind === 'add' && (
        <Dialog title={addLabel} onClose={() => setDialog(null)}>
          <PhotoScan clientId={clientId} lastMachine={latestScan?.machine ?? null} onSaved={afterSave} onCancel={() => setDialog(null)} />
        </Dialog>
      )}
      {dialog?.kind === 'edit' && (
        <Dialog title="Correct this scan" onClose={() => setDialog(null)}>
          <ScanForm clientId={clientId} scan={dialog.scan} onSaved={afterSave} onCancel={() => setDialog(null)} />
        </Dialog>
      )}
      {dialog?.kind === 'detail' && (
        <Dialog title={`Scan, ${fmtDay(dialog.scan.measured_on, true)}`} onClose={() => setDialog(null)}>
          <ScanDetail scan={dialog.scan} whoEntered={whoEntered} mayCorrect={mayCorrect(dialog.scan)}
            onEdit={() => setDialog({ kind: 'edit', scan: dialog.scan })} onDeleted={afterDelete} />
        </Dialog>
      )}
    </section>
  );
}

// The coach's "Compare two scans": To minus From, with a note when the machines differ
function Compare({ scans }) {
  const [fromId, setFromId] = useState(scans[1].id);
  const [toId, setToId] = useState(scans[0].id);
  const from = scans.find((s) => s.id === fromId);
  const to = scans.find((s) => s.id === toId);
  const c = compareScans(from, to);
  const select = (id, value, set, label) => (
    <div style={{ flex: 1, minWidth: 140 }}>
      <label htmlFor={id} style={{ display: 'block', fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>{label}</label>
      <select id={id} value={value} onChange={(e) => set(e.target.value)}
        style={{ ...mono, width: '100%', minHeight: 44, background: colors.bg, color: colors.text, border: `1px solid ${colors.borderStrong}`, borderRadius: radius.sm, padding: '0 8px' }}>
        {scans.map((s) => <option key={s.id} value={s.id}>{fmtDay(s.measured_on, true)} · {s.machine}</option>)}
      </select>
    </div>
  );
  return (
    <div style={{ background: colors.surface1, border: `1px solid ${colors.border}`, borderRadius: radius.card, padding: 16, margin: '16px 0' }}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Compare two scans</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {select('bs-from', fromId, setFromId, 'From')}
        {select('bs-to', toId, setToId, 'To')}
      </div>
      {c && (
        <div aria-live="polite" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginTop: 16 }}>
          {[['weight', 'Weight'], ['fat', 'Body fat'], ['muscle', 'Muscle']].map(([k, label]) => (
            <div key={k}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted }}>{label}</div>
              <div style={{ ...mono, fontSize: 18, fontWeight: 700, marginTop: 4 }}>{c[k] === null ? 'Not recorded' : formatChange(c[k], k)}</div>
            </div>
          ))}
        </div>
      )}
      {c && (
        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 12, lineHeight: 1.5 }}>
          {c.differentMachines
            ? `Different machines (${from.machine}, ${to.machine}): the differences may partly be the machine.`
            : `Same machine · ${to.machine}`}
          {c.differentMuscleKinds ? ' The two sheets also measure muscle differently.' : ''}
        </div>
      )}
    </div>
  );
}
